import { transient } from "aurelia-dependency-injection";
import { NotifyPropertyChangedBase, Reflection } from "./reflection";
import type { Disposable } from "aurelia-binding";
import { BindingEngine } from "aurelia-binding";
import { GuidHelper } from "./guid.helper";
import * as Enumerable from "linq";

@transient()
export class UnitOfWorkObserver<T = any> extends NotifyPropertyChangedBase {

    private subscriptions: Disposable[] = [];
    private paused: boolean = true;
    private skipProperties: Enumerable.IEnumerable<string>;
    public unitOfWorks: { [index: string]: UnitOfWorkItem } = {};

    constructor(private bindingEngine: BindingEngine, private item: T, skipProperties: string[] = [], 
        private arraysChangesInterceptor: (arrayName: string, elements: any[], areAdded: boolean) => any[] = null) {
        super();
        this.skipProperties = Enumerable.from(skipProperties).concat(["__metadata__"]);
        if (!Array.isArray(item)) {
            this.observe(item, this.unitOfWorks);
            if (item && this.skipProperties.all(x => x != "identifier")) {
                this.handleChange("identifier", item["identifier"], null, this.unitOfWorks);
            }
        } else {
            this.observeArray(item, this.unitOfWorks);
        }
    }

    public static getTrackingInstance<K = any>(bindingEngine: BindingEngine,
        object: K,
        skipProperties: string[] = [],
        arraysChangesInterceptor: (arrayName: string, elements: any[], areAdded: boolean) => any[] = null): UnitOfWorkObserver<K> {
        const newInstance = new UnitOfWorkObserver<K>(bindingEngine, object, skipProperties, arraysChangesInterceptor);
        newInstance.startTracking();
        return newInstance;

    }

    public skipProperty(skipProperty: string) {
        const array = this.skipProperties.toArray();
        this.skipProperties = Enumerable.from(array.concat([skipProperty]));
    }

    private observeArray(array: any, unitOfWorks: { [index: string]: any }): void {
        if (Array.isArray(array)) {
            const originalValue: any = structuredClone(array);
            // this is for arrays
            unitOfWorks.Array = {
                isArray: true
            };

            let addedInitially = [];
            if (array["__array_observer__"]
                && array["__array_observer__"].changeRecords
                && array["__array_observer__"].changeRecords[0]) {
                addedInitially = array["__array_observer__"].changeRecords[0].object as any[];
            }

            for (const arrayItem of Object.keys(array)) {
                if (addedInitially.some(x => x == array[arrayItem])) {
                    continue;
                }
                const innerUnitOfWorks: { [index: string]: UnitOfWorkItem } = {};
                const object = array[arrayItem];

                unitOfWorks.Array[array[arrayItem].id] = innerUnitOfWorks;
                if (Array.isArray(object)) {
                    this.observeArray(object, innerUnitOfWorks);
                } else {
                    this.observe(object, innerUnitOfWorks);
                }
            }

            this.subscriptions.push(this.bindingEngine.collectionObserver(array)
                .subscribe((changedValues: any) => {
                    if (!this.paused) {
                        for (const changedValue of changedValues)
                            this.getUnitOfWorksForArrayChange(null, unitOfWorks.Array, array, originalValue, changedValue);
                    }
                }));
        }
    }

    //here we will track the changes recursively
    private observe(obj: any, unitOfWorks: { [index: string]: any }): void {
        const properties = Reflection.getPropertiesOfObject(obj);
        for (const propertyName of properties) {
            if (this.skipProperties.any(x => x == propertyName)) {
                continue;
            }
            const objectValue = obj[propertyName];
            const originalValue: any = structuredClone(objectValue);

            //if its an array then we have to go through all objects and create its own unitOfWork by calling observe
            if (Array.isArray(objectValue)) {
                // this is for arrays
                unitOfWorks[propertyName] = {
                    isArray: true
                };
                for (const arrayItem of Object.keys(objectValue)) {
                    const innerUnitOfWorks: { [index: string]: UnitOfWorkItem } = {};
                    const object = obj[propertyName][arrayItem];

                    unitOfWorks[propertyName][object.id] = innerUnitOfWorks;
                    this.observe(object, innerUnitOfWorks);
                }
                this.subscriptions.push(this.bindingEngine.collectionObserver(obj[propertyName])
                    .subscribe((changedValues: any) => {
                        if (!this.paused && !this.skipProperties.any(x => x == propertyName)) {
                            if (this.skipProperties.all(x => x != "identifier")) {
                                this.handleChange("identifier", obj["identifier"], null, unitOfWorks);
                            }
                            for (const changedValue of changedValues)
                                this
                                    .getUnitOfWorksForArrayChange(propertyName, unitOfWorks[propertyName],
                                        obj[propertyName],
                                        originalValue,
                                        changedValue);
                        }
                    }));
            } else {
                // we had to handle date scenaio since we do not want date to be considered as a complex object such as Auction or Individual etc
                if (!(objectValue instanceof Date) && objectValue instanceof Object) {
                    // this is for complex values
                    const innerUnitOfWorks: { [index: string]: UnitOfWorkItem } = {};
                    unitOfWorks[propertyName] = innerUnitOfWorks;
                    this.observe(obj[propertyName], unitOfWorks[propertyName]);
                } else {
                    // this is for primitive values
                    this.subscriptions.push(this.bindingEngine.propertyObserver(obj, propertyName)
                        .subscribe((newValue: any) => {
                            if (this.paused || this.skipProperties.any(x => x == propertyName)) {
                                return;
                            }
                            if (this.skipProperties.all(x => x != "identifier")) {
                                this.handleChange("identifier", obj["identifier"], null, unitOfWorks);
                            }
                            this.handleChange(propertyName, newValue, originalValue, unitOfWorks);
                        }));
                }
            }
        }
    }

    getValue(item: any, property: string): any {
        const props: string[] = property ? property.split(".") : [];
        if (props.length == 0) {
            return null;
        }
        let nextEl = null;
        if (item && item instanceof Array) {
            nextEl = item.filter(x => x.id == props[0])[0];
        } else if (item) {
            nextEl = item[props[0]];
        }
        if (props.length === 1) {
            return nextEl;
        }
        const value = this.getValue(nextEl, property.substring(property.indexOf(".") + 1));
        return value;
    }

    setValue(item: any, property: string, valueToSet: any): any {
        if (property) {
            const props: string[] = property.split(".");
            let nextEl = null;
            if (item && item instanceof Array) {
                nextEl = item.filter(x => x.id == props[0])[0];
            } else if (item) {
                nextEl = item[props[0]];
            }
            
            if (props.length === 1) {
                item[property] = valueToSet;
            } else {
                this.setValue(nextEl, property.substring(property.indexOf(".") + 1), valueToSet);
            }
        }
    }

    getUnitOfWorkAssociated(property: string, uow: { [index: string]: UnitOfWorkItem }) {
        if (property) {
            const props: string[] = property.split(".");
            if (props.length === 1) {
                return uow;
            }
            const value = this.getUnitOfWorkAssociated(property.substring(property.indexOf(".") + 1), uow[props[0]].value ? uow[props[0]].value : uow[props[0]] as any);
            return value;
        }
        return null;
    }

    public addChange(propertyName: string | ((instance: T) => any),
        newValue: any,
        oldValue: any) {
        if (propertyName) {
            if (typeof propertyName == "function") {
                propertyName = Reflection.getPropertyPath(propertyName);
            }
            if (this.getValue(this.item, propertyName) != newValue) {
                this.setValue(this.item, propertyName, newValue);
            }
            const unitOfWorks = this.getUnitOfWorkAssociated(propertyName, this.unitOfWorks);
            if (propertyName) {
                const props: string[] = propertyName.split(".");
                this.handleChange(props[props.length - 1], newValue, oldValue, unitOfWorks);
            }
        }
    }

    private handleChange(propertyName: string,
        newValue: any,
        oldValue: any,
        unitOfWorks: { [index: string]: UnitOfWorkItem }): void {
        const existing: UnitOfWorkItem = unitOfWorks[propertyName];

        if (newValue == oldValue && !existing) {
            return;
        }

        this.onPropertyChanged<UnitOfWorkObserver>(z => z.unitOfWorks);

        if (existing) {
            if (newValue == existing.oldValue) {
                delete unitOfWorks[propertyName];
                return;
            }
            existing.value = newValue;
        } else {
            // non array items can only be modified
            unitOfWorks[propertyName] = {
                action: UnitOfWorkAction.Modified,
                oldValue,
                value: newValue
            };
        }
    }

    public dispose(): void {
        for (const subscription of this.subscriptions) {
            if (subscription && subscription.dispose) {
                subscription.dispose();
            }
        }

        this.stopTracking();
        this.unitOfWorks = {};
        this.subscriptions = [];
    }

    public startTracking(): void {
        this.paused = false;
    }

    public stopTracking(): void {
        this.paused = true;
    }

    public clearChanges(): void {
        this.unitOfWorks = {};
    }

    public getChanges(): { [index: string]: UnitOfWorkItem } {
        return this.getChangesInternal(this.unitOfWorks);
    }

    public get getHasChanges(): boolean {
        let hasChanges = false;
        if (this.unitOfWorks) {
            hasChanges = this.hasChangesInternal(this.unitOfWorks, false);
        }
        return hasChanges;
    }


    public hasChanges(skipIdentifiers: boolean = false): boolean {
        let hasChanges = false;
        if (this.unitOfWorks) {
            hasChanges = this.hasChangesInternal(this.unitOfWorks, skipIdentifiers);
        }
        return hasChanges;
    }


    private hasChangesInternal(uow: { [index: string]: UnitOfWorkItem }, skipIdentifiers: boolean) {
        let hasChanges = false;
        if (!uow) {
            return true;
        }
        for (const key of Object.keys(uow)) {
            if (key == "identifier" && skipIdentifiers) {
                continue;
            }
            const item: any = uow[key];
            const objUOW: { [index: string]: UnitOfWorkItem } = item as { [index: string]: UnitOfWorkItem };
            const arrayUOW: any = item as any;
            const otherUOW: UnitOfWorkItem = item as UnitOfWorkItem;

            if (otherUOW && otherUOW.action > -1) {
                hasChanges = true;
            } else if (arrayUOW && arrayUOW.isArray && Object.keys(objUOW).length > 1) {
                for (const item1 of Object.keys(arrayUOW)) { //when its an array we recurse into the individual unit of works
                    if (item1 != "isArray") {
                        hasChanges = this.hasChangesInternal(arrayUOW[item1], skipIdentifiers);
                        if (hasChanges) {
                            break;
                        }
                    }
                }
            } else if (objUOW && Object.keys(objUOW).length > 0) {
                hasChanges = this.hasChangesInternal(objUOW, skipIdentifiers);
                if (hasChanges) {
                    break;
                }
            }

            if (hasChanges) {
                break;
            }
        }
        return hasChanges;
    }

    private getChangesInternal(uow: { [index: string]: UnitOfWorkItem }) {
        const changes = {};
        if (!uow) {
            return changes;
        }
        for (const key of Object.keys(uow)) {
            const item: any = uow[key];
            const objUOW: { [index: string]: UnitOfWorkItem } = item as { [index: string]: UnitOfWorkItem };
            const arrayUOW: any = item as any;
            const otherUOW: UnitOfWorkItem = item as UnitOfWorkItem;

            if (otherUOW && otherUOW.action > -1) {
                changes[key] = uow[key];
                if (this.skipProperties && this.skipProperties.any()) {
                    this.skipProperties.forEach(skipProperty => {
                        if (uow[key].value) {
                            const hasProperty = Object.prototype.hasOwnProperty.call(uow[key].value, skipProperty);
                            if (hasProperty) {
                                delete uow[key].value[skipProperty];
                            }
                        }
                    });
                }
            } else if (arrayUOW && arrayUOW.isArray && Object.keys(objUOW).length > 1) {
                for (const item1 of Object.keys(arrayUOW)) {
                    if (item1 != "isArray") {
                        if (this.hasChangesInternal(arrayUOW[item1], false)) {
                            //when it's an array we check for changes and add the changes to the dictionary of changes
                            if (!changes[key]) {
                                changes[key] = {
                                    isArray: true
                                };
                            }
                            changes[key][item1] = this.getChangesInternal(arrayUOW[item1]);
                        }
                    }
                }
                //this is how we get changes for the complex object
            } else if (objUOW && Object.keys(objUOW).length > 0) {
                if (this.hasChangesInternal(objUOW, false)) {
                    changes[key] = this.getChangesInternal(objUOW);
                }
            }
        }
        return changes;
    }

    //this is to handle adding and removing in an array
    private getUnitOfWorksForArrayChange(arrayName: string, unitOfWorks: any[], originalObject: any[], object: any[], changedValues: any):
        void {

        if (changedValues && changedValues.addedCount > 0) {

            let addedItems: any[] = originalObject.slice(changedValues.index,
                changedValues.index + changedValues.addedCount);
            addedItems = this.arraysChangesInterceptor ? this.arraysChangesInterceptor(arrayName, addedItems, true) : addedItems;
            for (const addedItem of addedItems) {
                const innerUnitOfWorks: { [index: string]: UnitOfWorkItem } = {
                    "": {
                        action: UnitOfWorkAction.Added,
                        value: structuredClone(addedItem),
                        oldValue: null
                    }
                };

                const generateGuid = GuidHelper.generateGuid();
                unitOfWorks[generateGuid] = (innerUnitOfWorks);
                addedItem.uow = generateGuid;
                this.observe(addedItem, innerUnitOfWorks);
            }
        }

        if (changedValues && changedValues.removed && changedValues.removed.length > 0) {
            let removedItems = changedValues.removed;
            removedItems = this.arraysChangesInterceptor ? this.arraysChangesInterceptor(arrayName, removedItems, false) : removedItems;
            for (const removedItem of removedItems) {

                if (removedItem.uow) {
                    delete unitOfWorks[removedItem.uow];
                } else if (removedItem.id > 0) {
                    const innerUnitOfWorks: { [index: string]: UnitOfWorkItem } = {
                        "": {
                            action: UnitOfWorkAction.Removed,
                            value: removedItem,
                            oldValue: null
                        }
                    };
                    unitOfWorks[removedItem.id] = innerUnitOfWorks;
                }
            }
        }
    }
}

export class UnitOfWorkItem {
    action: UnitOfWorkAction;
    value: any;
    oldValue: any;
}

export enum UnitOfWorkAction {
    Unknown = -1,
    Added = 0,
    Removed = 1,
    Modified = 2
}

export interface BaseUnitOfWorkRequest {

    changes: { [index: string]: any };
    id: number;
}
